import React, { useEffect, useState } from 'react';
import { useThemeMode } from '../ThemeContext';
import { usePermissions } from '../contexts/PermissionContext';
import { useLocation, useNavigate } from 'react-router-dom';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { getProfileCompanyPicUrl } from '../helpers/url_helper';
import complimateWhite from '../assets/complimate_white.svg';
import projectsIcon from '../assets/Projects.svg';
import openFolderIcon from '../assets/Open Folder.svg';
import icsDocument from '../assets/icsdocument.svg';
import icSteam from '../assets/icsteam.svg';
import icsSubscription from '../assets/icsubscription.svg';
import icsSetting from '../assets/icssetting.svg';
import icsDashboard from '../assets/icsdashboard.svg';
import addRoleIcon from '../assets/add_role.svg';
import userIcon from '../assets/user.svg';
import '../styles/styles.css';
import '../styles/manage-employee.css';

// Hide scrollbar for sidebar
const sidebarNoScroll = `
  .sidebar::-webkit-scrollbar { display: none; }
`;
if (typeof document !== 'undefined') {
  let style = document.getElementById('sidebar-no-scroll-style');
  if (!style) {
    style = document.createElement('style');
    style.id = 'sidebar-no-scroll-style';
    style.innerHTML = sidebarNoScroll;
    document.head.appendChild(style);
  }
}

function Sidebar(props) {
  const { mode } = useThemeMode();
  const { hasReadPermission, hasAnyPermission } = usePermissions();
  const location = useLocation();
  const navigate = useNavigate();
  const { isOpen = false, onClose } = props;
  const [companyLogo, setCompanyLogo] = useState(null);
  const [companyName, setCompanyName] = useState('');
  const [expandedMenu, setExpandedMenu] = useState(null);

  useEffect(() => {
    const getUserData = () => {
      try {
        const userData = JSON.parse(localStorage.getItem('userData'));
        if (userData && userData.company_detail) {
          setCompanyLogo(userData.company_detail.company_logo || null);
          setCompanyName(userData.company_detail.name || '');
        }
      } catch (e) {
        setCompanyLogo(null);
        setCompanyName('');
      }
    };

    getUserData();

    // Listen for profile updates
    const handleProfileUpdate = (event) => {
      const updatedUserData = event.detail;
      if (updatedUserData && updatedUserData.company_detail) {
        setCompanyLogo(updatedUserData.company_detail.company_logo || null);
        setCompanyName(updatedUserData.company_detail.name || '');
      }
    };

    window.addEventListener('userProfileUpdated', handleProfileUpdate);

    return () => {
      window.removeEventListener('userProfileUpdated', handleProfileUpdate);
    };
  }, []);

  // Auto-expand Manage Employee menu when sub-items are active
  useEffect(() => {
    if (location.pathname === '/manage-employee/employees' || 
        location.pathname === '/manage-employee/roles' ||
        location.pathname === '/manage-employee/role-permission') {
      setExpandedMenu('manageEmployee');
    }
  }, [location.pathname]);

  const handleNavigation = (path) => {
    navigate(path);
    onClose?.();
  };

  const handleMenuToggle = (menuName) => {
    setExpandedMenu(expandedMenu === menuName ? null : menuName);
  };

  const isManageEmployeeActive = location.pathname.startsWith('/manage-employee') && 
                                !location.pathname.includes('/employees') && 
                                !location.pathname.includes('/roles') && 
                                !location.pathname.includes('/role-permission');

  return (
    <aside className={`sidebar${isOpen ? ' sidebar-mobile-open' : ''}`} style={{
      position: 'fixed',
      top: 0,
      left: 0,
      height: '100vh',
      width: 235,
      overflowY: 'auto',
      zIndex: 101,
      scrollbarWidth: 'none', // Firefox
      ...props.style
    }}>
      <div>
        {/* Header: company logo only, centered */}
        <div className="sidebar-header-center">
          {companyLogo ? (
            <img
              src={getProfileCompanyPicUrl(companyLogo)}
              alt="Company Logo"
              className="sidebar-header-logo"
            />
          ) : companyName ? (
            <div className="sidebar-header-initials">
              {companyName.length === 0 ? '' : (() => {
                const words = companyName.trim().split(/\s+/);
                if (words.length === 1) return companyName.substring(0, 2).toUpperCase();
                return words.slice(0, 2).map(w => w.charAt(0)).join('').toUpperCase();
              })()}
            </div>
          ) : (
            <div className="sidebar-logo">Complimate</div>
          )}
        </div>

        <nav className="sidebar-menu">
          {/* Main navigation */}
          <SidebarItem 
            icon={<img src={icsDashboard} alt="Dashboard" className="sidebar-icon" />} 
            label="Dashboard" 
            active={location.pathname === '/dashboard'}
            onClick={() => handleNavigation('/dashboard')}
          />
          {hasAnyPermission('assistant') && (
            <SidebarItem 
              icon={<img src={complimateWhite} alt="Complimate AI" className="sidebar-icon sidebar-icon-ai" />} 
              label="Complimate AI" 
              active={location.pathname === '/assistant'}
              onClick={() => handleNavigation('/assistant')}
            />
          )}
          {hasAnyPermission('document') && (
            <SidebarItem 
              icon={<img src={icsDocument} alt="Documents" className="sidebar-icon" />} 
              label="Documents" 
              active={location.pathname === '/documents'}
              onClick={() => handleNavigation('/documents')}
            />
          )}
          {hasAnyPermission('document') && (
            <SidebarItem
              icon={<img src={icsDocument} alt="Company Form" className="sidebar-icon" />}
              label="Company Form"
              active={location.pathname.startsWith('/company-forms')}
              onClick={() => handleNavigation('/company-forms')}
            />
          )}

          {/* PROJECTS section */}
          <SidebarSectionHeader label="PROJECTS" />
          {hasAnyPermission('task') && (
            <>
              <SidebarItem 
                icon={<img src={openFolderIcon} alt="Projects" className="sidebar-icon" />} 
                label="Projects" 
                active={location.pathname === '/projects'}
                onClick={() => handleNavigation('/projects')}
              />
              <SidebarItem 
                icon={<img src={projectsIcon} alt="Manage Task" className="sidebar-icon" />} 
                label="Manage Task" 
                active={location.pathname === '/manage-task'}
                onClick={() => handleNavigation('/manage-task')}
              />
            </>
          )}

          {/* ADMIN section: Manage Team, Subscription, Settings (no duplicates from main) */}
          <SidebarSectionHeader label="ADMIN" />
          {hasAnyPermission('employee') && (
            <>
              <SidebarItem 
                icon={<img src={icSteam} alt="Manage Team" className="sidebar-icon" />} 
                label="Manage Team" 
                active={isManageEmployeeActive}
                chevron 
                expanded={expandedMenu === 'manageEmployee'}
                onClick={() => handleMenuToggle('manageEmployee')}
              />
              {expandedMenu === 'manageEmployee' && (
                <div className="sidebar-subitems">
                  <SidebarItem 
                    icon={<img src={userIcon} alt="Employees" className="sidebar-icon" />} 
                    label="Employees" 
                    active={location.pathname === '/manage-employee/employees'}
                    onClick={() => handleNavigation('/manage-employee/employees')}
                    isSubItem={true}
                  />
                  <SidebarItem 
                    icon={<img src={addRoleIcon} alt="Roles" className="sidebar-icon" />} 
                    label="Roles" 
                    active={location.pathname === '/manage-employee/roles' || location.pathname === '/manage-employee/role-permission'}
                    onClick={() => handleNavigation('/manage-employee/roles')}
                    isSubItem={true}
                  />
                </div>
              )}
            </>
          )}
          <SidebarItem 
            icon={<img src={icsDocument} alt="Digital Forms" className="sidebar-icon" />} 
            label="Digital Forms" 
            active={location.pathname.startsWith('/digital-form-management')}
            onClick={() => handleNavigation('/digital-form-management')}
          />
          {(hasAnyPermission('subscrptionplan') || hasAnyPermission('subscriptionplan')) && (
            <SidebarItem 
              icon={<img src={icsSubscription} alt="Subscription" className="sidebar-icon" />} 
              label="Subscription" 
              active={location.pathname === '/subscription'}
              onClick={() => handleNavigation('/subscription')}
            />
          )}
          <SidebarItem 
            icon={<img src={icsSetting} alt="Settings" className="sidebar-icon" />} 
            label="Settings" 
            active={location.pathname === '/settings'}
            onClick={() => handleNavigation('/settings')}
          />
        </nav>
      </div>
    </aside>
  );
}

function SidebarSectionHeader({ label }) {
  return <div className="sidebar-section-header">{label}</div>;
}

function SidebarItem({ icon, label, active, chevron, expanded, onClick, isSubItem = false }) {
  return (
    <div 
      className={`sidebar-item${active ? ' active' : ''}${isSubItem ? ' sub-item' : ''}`}
      onClick={onClick}
      style={{ 
        cursor: onClick ? 'pointer' : 'default',
        paddingLeft: isSubItem ? '20px' : '16px',
        fontSize: isSubItem ? '13px' : '14px'
      }}
    > 
      <span className="sidebar-icon">{icon}</span>
      <span style={{ flex: 1 }}>{label}</span>
      {chevron && <ChevronRightIcon className="sidebar-chevron" style={{ 
        transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
        transition: 'transform 0.2s ease'
      }} />}
    </div>
  );
}

export default Sidebar; 